import { FC, RefObject, useState } from "react";

import {
  EvalError,
  EvalRetryError,
  EvalSampleLimit,
} from "@tsmono/inspect-common";
import { Icon } from "@tsmono/react/icons";

import { RetryAttemptCard } from "./retry-display/RetryAttemptCard";
import { RetryTerminalAnchor } from "./retry-display/RetryTerminalAnchor";
import styles from "./SampleRetriedErrors.module.css";

interface SampleRetriedErrorsProps {
  id: string;
  retries: EvalRetryError[];
  // Final-run outcome — present when the last attempt also failed (retry
  // limit reached) or terminated on a sample limit.
  error?: EvalError | null;
  limit?: EvalSampleLimit | null;
  scrollRef: RefObject<HTMLDivElement | null>;
}

export const SampleRetriedErrors: FC<SampleRetriedErrorsProps> = ({
  id,
  retries,
  error,
  limit,
  scrollRef,
}) => {
  // Accordion: default to the most recent failure (closest to the final run).
  // Callers must pass key={id} so the state resets when the sample changes
  // (the same component instance can be reused on the inline display path).
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    retries.length - 1
  );

  const onToggleOpen = (index: number) => {
    setExpandedIndex((cur) => (cur === index ? null : index));
  };

  return (
    <div className={styles.panel}>
      <div className={styles.sectionLabel}>Retry Attempts</div>
      <div className={styles.timeline}>
        <div className={styles.rail} aria-hidden="true" />
        <div className={styles.items}>
          {retries.map((retry, index) => (
            <div className={styles.row} key={index}>
              <div className={styles.dotGutter} aria-hidden="true">
                <Icon name="error" className={styles.statusIcon} />
              </div>
              <RetryAttemptCard
                retry={retry}
                attemptNumber={index + 1}
                isOpen={expandedIndex === index}
                onToggleOpen={() => onToggleOpen(index)}
                listId={`sample-error-retries-${id}-${index}`}
                scrollRef={scrollRef}
              />
            </div>
          ))}
          <RetryTerminalAnchor
            retryCount={retries.length}
            error={error}
            limit={limit}
          />
        </div>
      </div>
    </div>
  );
};
